import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Contact, contactTypeTranslation } from "@/models/ContactModel.ts";
import { userFormattedName } from "@/models/UserModel.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { Card } from "@/uiKit/atoms/Card.tsx";
import { CardContent } from "@/uiKit/atoms/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/CardHeader.tsx";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

export type ContactViewProps = {
  contact: Contact;
  setEditing?: (contact: Contact) => void;
  onDelete?: (contact: Contact) => void;
  className?: string;
};

export const ContactView: React.FC<ContactViewProps> = ({ contact, setEditing, onDelete, className }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  return (
    <Card id={`contact-${contact.id}`} className={cx(styles.container, className)}>
      <CardHeader title="" className={styles.header} />
      <CardContent className={styles.content}>
        <Typography variant="body" className={cx(styles.title, styles.row)}>
          {contact.firstName && contact.lastName
            ? userFormattedName(contact.lastName, contact.firstName)
            : t("contact.noName")}
        </Typography>
        <Typography variant="body">{t(contactTypeTranslation(contact.contactType))}</Typography>
        {contact.isEmergencyContact ? (
          <Typography variant="body" className={styles.emergency}>
            {t("contact.emergencyContact").slice(0, -1)}
          </Typography>
        ) : null}
        <div className={styles.row}>
          <Icons.phone className={styles.icon} />
          <Typography variant="body">{contact.phone}</Typography>
        </div>
        {contact.email ? (
          <div className={styles.row}>
            <Icons.email className={styles.icon} />
            <Typography variant="body">{contact.email}</Typography>
          </div>
        ) : null}
        {contact.comment ? (
          <Typography variant="body" className={styles.comment}>
            {contact.comment}
          </Typography>
        ) : null}
      </CardContent>
      <CardFooter className={styles.actions}>
        {setEditing ? (
          <Button
            data-action="medical-file.contact.edit"
            id="edit-contact"
            variant="outlined"
            onClick={() => setEditing(contact)}
          >
            <Icons.edit className={styles.iconAction} />
          </Button>
        ) : null}
        {onDelete ? (
          <Button
            data-action="medical-file.contact.delete"
            id="delete-contact"
            variant="outlined"
            onClick={() => onDelete(contact)}
          >
            <Icons.trash className={styles.iconAction} />
          </Button>
        ) : null}
      </CardFooter>
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(8)};
  `,
  icon: css`
    width: ${FontSizes.body};
    height: ${FontSizes.body};
  `,
  iconAction: css`
    width: ${FontSizes.bodyLarge};
    height: ${FontSizes.bodyLarge};
  `,
  header: css`
    padding: 0;
  `,
  content: css`
    display: flex;
    flex-direction: column;
    padding: 0;
    flex: 1;
  `,
  actions: css`
    justify-content: flex-start;
    padding: 0;
    column-gap: ${theme.spacing(4)};
  `,
  title: css({
    fontWeight: "bold",
  }),
  row: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(8)};
  `,
  emergency: css`
    color: ${theme.palette.warning.main};
    font-weight: bold;
  `,
  comment: css`
    color: ${theme.palette.text.secondary};
    font-style: italic;
  `,
});
